import React, { useEffect, useMemo, useState } from "react";
import AppHeader from "../../components/AppHeader";
import { buildHierarchicalList } from "../../components/staff/constants";
import { fetchLeaveSummary, buildLeaveSummaryExcelUrl } from "../../services/webReportsService";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { myanmarFontBase64 } from "../../utils/myanmarFontBase64";

export default function LeaveSummaryReport() {
  const tenant = localStorage.getItem("tenant") || "";
  const token = sessionStorage.getItem("token") || "";

  // Filter States
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [branchId, setBranchId] = useState("");
  const [orgNodeId, setOrgNodeId] = useState("");
  const [minDays, setMinDays] = useState("");
  const [presetRange, setPresetRange] = useState("this_month");

  // Metadata Dropdown States
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);

  // Data Grid States
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [error, setError] = useState("");

  const handlePresetChange = (preset) => {
    setPresetRange(preset);
    if (preset === "custom") return;

    const today = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const toKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    if (preset === "today") {
      setDateFrom(toKey(today));
      setDateTo(toKey(today));
    } else if (preset === "yesterday") {
      const yesterday = new Date();
      yesterday.setDate(today.getDate() - 1);
      setDateFrom(toKey(yesterday));
      setDateTo(toKey(yesterday));
    } else if (preset === "this_week") {
      const currentDay = today.getDay();
      const distanceToMonday = currentDay === 0 ? -6 : 1 - currentDay;
      const monday = new Date(today);
      monday.setDate(today.getDate() + distanceToMonday);

      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);

      setDateFrom(toKey(monday));
      setDateTo(toKey(sunday));
    } else if (preset === "this_month") {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      setDateFrom(toKey(firstDay));
      setDateTo(toKey(new Date()));
    } else if (preset === "last_month") {
      const firstDayLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lastDayLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      setDateFrom(toKey(firstDayLastMonth));
      setDateTo(toKey(lastDayLastMonth));
    } else if (preset === "this_year") {
      const firstDayYear = new Date(today.getFullYear(), 0, 1);
      setDateFrom(toKey(firstDayYear));
      setDateTo(toKey(new Date()));
    }
  };

  // Set default date range: first day of current month to today
  useEffect(() => {
    const pad = (n) => String(n).padStart(2, "0");
    const toKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);

    setDateFrom(toKey(firstDay));
    setDateTo(toKey(now));
  }, []);

  // Fetch branches and departments dropdown data on mount
  useEffect(() => {
    const fetchMetadata = async () => {
      const config = {
        withCredentials: true,
        headers: {
          "X-Tenant": tenant,
          "Authorization": `Bearer ${token}`
        }
      };
      try {
        const [branchRes, nodeRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL1}/som/branches`, config),
          axios.get(`${process.env.REACT_APP_API_URL1}/som/nodes`, config)
        ]);

        if (Array.isArray(branchRes.data?.data)) {
          setBranches(branchRes.data.data);
        }
        if (Array.isArray(nodeRes.data?.data)) {
          setDepartments(buildHierarchicalList(nodeRes.data.data));
        }
      } catch (err) {
        console.error("Failed to load metadata filters:", err);
      }
    };

    if (tenant && token) {
      fetchMetadata();
    }
  }, [tenant, token]);

  const canSearch = useMemo(() => Boolean(dateFrom && dateTo), [dateFrom, dateTo]);
  const canAutoLoad = useMemo(() => Boolean(tenant && dateFrom && dateTo), [tenant, dateFrom, dateTo]);

  const onSearch = async () => {
    if (!tenant) {
      setError("Missing tenant configuration.");
      return;
    }

    setError("");
    setLoading(true);
    try {
      const res = await fetchLeaveSummary({
        date_from: dateFrom,
        date_to: dateTo,
        branch_id: branchId || undefined,
        org_node_id: orgNodeId || undefined,
        min_days: minDays || undefined
      });

      if (res.success) {
        setLeaveTypes(res.leaveTypes || []);
        setRows(res.rows || []);
      } else {
        throw new Error(res.message || "Failed to load report");
      }
    } catch (e) {
      setError(e?.message || "Failed to load report");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canAutoLoad) onSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAutoLoad]);

  const onExportExcel = async () => {
    if (!tenant) {
      setError("Missing tenant configuration.");
      return;
    }

    setError("");
    setLoading(true);
    try {
      const url = buildLeaveSummaryExcelUrl({
        date_from: dateFrom,
        date_to: dateTo,
        branch_id: branchId || undefined,
        org_node_id: orgNodeId || undefined,
        min_days: minDays || undefined
      });

      const res = await fetch(url, {
        credentials: "include",
        headers: { "X-Tenant": tenant },
      });

      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `Export failed: HTTP ${res.status}`);
      }

      const blob = await res.blob();
      const a = document.createElement("a");
      const objectUrl = URL.createObjectURL(blob);
      a.href = objectUrl;
      a.download = `leave_summary_${dateFrom}_${dateTo}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      setError(e?.message || "Export failed");
    } finally {
      setLoading(false);
    }
  };

  const onExportPDF = () => {
    try {
      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

      if (myanmarFontBase64) {
        doc.addFileToVFS("Padauk-Regular.ttf", myanmarFontBase64);
        doc.addFont("Padauk-Regular.ttf", "Padauk", "normal");
        doc.addFont("Padauk-Regular.ttf", "Padauk", "bold");
      }
      const defaultFont = myanmarFontBase64 ? "Padauk" : "helvetica";

      // Report Header info
      doc.setFont(defaultFont, "bold");
      doc.setFontSize(16);
      doc.setTextColor(30, 41, 59); // slate-800
      doc.text("Leave Summary Report", 14, 15);

      doc.setFont(defaultFont, "normal");
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Period: ${dateFrom} to ${dateTo}`, 14, 21);

      // Build table headers and body
      const tableHeaders = ["No.", "Employee ID", "Staff Name", "Branch", "Dept"];
      leaveTypes.forEach(lt => tableHeaders.push(lt.type_name));
      tableHeaders.push("Total");

      const tableRows = rows.map((r, index) => {
        const rowData = [
          index + 1,
          r.employee_id || r.staff_code || "",
          r.full_name,
          r.branch_name || "-",
          r.dept_name || "-"
        ];
        leaveTypes.forEach(lt => {
          rowData.push(r.leaves[lt.type_code] || "-");
        });
        rowData.push(r.total_days);
        return rowData;
      });

      autoTable(doc, {
        startY: 26,
        head: [tableHeaders],
        body: tableRows,
        theme: "striped",
        headStyles: { fillColor: [79, 70, 229], font: defaultFont, fontStyle: "bold" },
        styles: { fontSize: 8, font: defaultFont },
        columnStyles: {
          0: { cellWidth: 10 },
          1: { cellWidth: 25 },
          2: { cellWidth: 35 },
          3: { cellWidth: 25 },
          4: { cellWidth: 25 },
        }
      });

      doc.save(`leave_summary_${dateFrom}_${dateTo}.pdf`);
    } catch (e) {
      setError(e?.message || "PDF Export failed");
    }
  };

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f8fafc" }}>
      <AppHeader />

      <div style={{ padding: "24px 32px", maxWidth: 1400, margin: "0 auto" }}>
        <h2 style={{ fontSize: "24px", fontWeight: 800, color: "#1e293b", marginBottom: 4 }}>
          Leave Summary Report
        </h2>
        <p style={{ color: "#64748b", margin: "0 0 24px 0" }}>
          Aggregated leave days taken by staff per leave type with advanced filters.
        </p>

        {/* Filters Panel */}
        <div style={{
          backgroundColor: "#ffffff",
          padding: "20px 24px",
          borderRadius: 8,
          border: "1px solid #e2e8f0",
          display: "flex",
          gap: 16,
          flexWrap: "wrap",
          alignItems: "end",
          marginBottom: 24
        }}>
          <div>
            <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#475569", marginBottom: 6 }}>Quick Range</label>
            <select 
              value={presetRange} 
              onChange={(e) => handlePresetChange(e.target.value)}
              style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: "14px", minWidth: 145 }}
            >
              <option value="custom">Custom Range</option>
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="this_week">This Week</option>
              <option value="this_month">This Month</option>
              <option value="last_month">Last Month</option>
              <option value="this_year">This Year</option>
            </select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#475569", marginBottom: 6 }}>Date From</label>
            <input 
              type="date" 
              value={dateFrom} 
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPresetRange("custom");
              }} 
              style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: "14px" }}
            />
          </div>
          <div>
            <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#475569", marginBottom: 6 }}>Date To</label>
            <input 
              type="date" 
              value={dateTo} 
              onChange={(e) => {
                setDateTo(e.target.value);
                setPresetRange("custom");
              }} 
              style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: "14px" }}
            />
          </div>
          <div>
            <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#475569", marginBottom: 6 }}>Branch</label>
            <select 
              value={branchId} 
              onChange={(e) => setBranchId(e.target.value)}
              style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: "14px", minWidth: 150 }}
            >
              <option value="">All Branches</option>
              {branches.map(b => <option key={b.id} value={b.id}>{b.branch_name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#475569", marginBottom: 6 }}>Department</label>
            <select 
              value={orgNodeId} 
              onChange={(e) => setOrgNodeId(e.target.value)}
              style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: "14px", minWidth: 160 }}
            >
              <option value="">All Departments</option>
              {departments.map(d => (
                <option key={d.id} value={d.id}>
                  {' '.repeat((d._depth || 0) * 2)}{d._depth > 0 ? '└ ' : ''}{d.node_name}{d.level_type ? ` (${d.level_type})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#475569", marginBottom: 6 }}>Min Leave Days</label>
            <input 
              type="number"
              min="0"
              step="0.5"
              placeholder="e.g. 4"
              value={minDays}
              onChange={(e) => setMinDays(e.target.value)}
              style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: "14px", width: 110 }}
            />
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button 
              onClick={onSearch} 
              disabled={loading || !canSearch}
              style={{
                backgroundColor: "primary.dark",
                color: "#ffffff",
                padding: "8px 16px",
                borderRadius: 6,
                border: "none",
                fontSize: "14px",
                fontWeight: 600,
                cursor: "pointer",
                opacity: (loading || !canSearch) ? 0.6 : 1
              }}
            >
              Search
            </button>
            <button 
              onClick={onExportExcel} 
              disabled={loading || rows.length === 0 || !canSearch}
              style={{
                backgroundColor: "#10b981",
                color: "#ffffff",
                padding: "8px 16px",
                borderRadius: 6,
                border: "none",
                fontSize: "14px",
                fontWeight: 600,
                cursor: "pointer",
                opacity: (loading || rows.length === 0 || !canSearch) ? 0.6 : 1
              }}
            >
              Export Excel
            </button>
            <button 
              onClick={onExportPDF} 
              disabled={loading || rows.length === 0 || !canSearch}
              style={{
                backgroundColor: "#e11d48",
                color: "#ffffff",
                padding: "8px 16px",
                borderRadius: 6,
                border: "none",
                fontSize: "14px",
                fontWeight: 600,
                cursor: "pointer",
                opacity: (loading || rows.length === 0 || !canSearch) ? 0.6 : 1
              }}
            >
              Export PDF
            </button>
          </div>
        </div>

        {error && <div style={{ marginBottom: 16, color: "#ef4444", fontSize: "14px", fontWeight: 600 }}>{error}</div>}
        {loading && <div style={{ marginBottom: 16, color: "primary.dark", fontSize: "14px" }}>Loading report data...</div>}

        {/* Data Grid */}
        <div style={{
          backgroundColor: "#ffffff",
          borderRadius: 8,
          border: "1px solid #e2e8f0",
          overflow: "hidden",
          boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)"
        }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px", color: "#334155" }}>
              <thead>
                <tr style={{ backgroundColor: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700 }}>No.</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700 }}>Employee ID</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700 }}>Staff Name</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700 }}>Branch</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700 }}>Dept</th>
                  {leaveTypes.map((lt) => (
                    <th key={lt.type_code} style={{ padding: "12px 16px", textAlign: "center", fontWeight: 700 }}>
                      {lt.type_name}
                    </th>
                  ))}
                  <th style={{ padding: "12px 16px", textAlign: "center", fontWeight: 700 }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, index) => (
                  <tr key={r.staff_code} style={{ borderBottom: "1px solid #f1f5f9", hover: { backgroundColor: "#f8fafc" } }}>
                    <td style={{ padding: "12px 16px" }}>{index + 1}</td>
                    <td style={{ padding: "12px 16px", fontWeight: 600, color: "#0f172a" }}>{r.employee_id || r.staff_code || ""}</td>
                    <td style={{ padding: "12px 16px" }}>{r.full_name}</td>
                    <td style={{ padding: "12px 16px" }}>{r.branch_name || "-"}</td>
                    <td style={{ padding: "12px 16px" }}>{r.dept_name || "-"}</td>
                    {leaveTypes.map((lt) => (
                      <td key={lt.type_code} style={{ padding: "12px 16px", textAlign: "center", fontWeight: r.leaves[lt.type_code] ? 600 : 400 }}>
                        {r.leaves[lt.type_code] || "-"}
                      </td>
                    ))}
                    <td style={{ padding: "12px 16px", textAlign: "center", fontWeight: 700, color: "#1e1b4b" }}>
                      {r.total_days}
                    </td>
                  </tr>
                ))}

                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={leaveTypes.length + 5} style={{ padding: "24px", textAlign: "center", color: "#64748b" }}>
                      No matching records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
